import React, { useState, useEffect } from "react";
import { API, graphqlOperation, Storage } from "aws-amplify";
import { searchFiles } from "../../../graphql/queries";
import {
  Typography,
  Avatar,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Backdrop,
} from "@material-ui/core";
import { useNotify, Title } from "react-admin";
import { formatDistanceToNow } from "date-fns";
import { makeStyles } from "@material-ui/core/styles";
import { ChevronRightIcon } from "../../../components/common/iconography";
import FolderSpecialOutlinedIcon from "@material-ui/icons/FolderSpecialOutlined";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import { logError } from "../../../utils/appUtils";
import SearchInput from "../../../components/common/SearchInput";
import CrewCard from "../../../components/common/CrewCard";
import { UserFileVisibility } from "../../../utils/constants";
import { ForceTitle } from "../../../components/common/ForceTitle";

const useStyles = makeStyles((theme) => ({
  listHeader: {
    marginTop: theme.spacing(1),
  },
  inventoryCardAvatar: {
    color: theme.palette.getContrastText(theme.palette.secondary.main),
    backgroundColor: theme.palette.secondary.main,
    width: theme.spacing(6),
    height: theme.spacing(6),
  },
  progress: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 300,
  },
  backdrop: {
    zIndex: 90001,
  },
}));

export const Documents = () => {
  const classes = useStyles();
  const notify = useNotify();
  const [loading, setLoading] = useState();
  const [term, setTerm] = useState("");
  const [docs, setDocs] = useState([]);

  useEffect(() => {
    getDocs(term);
  }, []);

  const getDocs = async (term) => {
    try {
      setLoading(true);
      const searchInput = {
        sort: { field: "displayName", direction: "asc" },
        filter: {
          reloadYardId: { eq: window.appUser.reloadYardId },
          visibility: { lte: UserFileVisibility[window.appUser.type] },
        },
        limit: 20,
      };
      if (term) {
        searchInput.filter.or = [
          { displayName: { matchPhrasePrefix: `.*${term}.*` } },
          { displayName: { wildcard: `*${term}*` } },
          { rawFileName: { matchPhrasePrefix: `.*${term}.*` } },
          { rawFileName: { wildcard: `*${term}*` } },
        ];
      }
      const response = await API.graphql(
        graphqlOperation(searchFiles, searchInput)
      );
      let data = response.data.searchFiles.items;
      setDocs(data);
    } catch (err) {
      logError("Documents:getDocs", err, notify);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    getDocs(term);
  };

  const handleSelect = async (itm) => {
    try {
      const response = await Storage.get(itm.file.key);
      window.open(response);
    } catch (err) {
      const error = `Storage error getting ${itm.fullName}`;
      logError("Documents:handleSelect", error, notify);
    }
  };

  const clear = () => {
    setTerm("");
    getDocs(null);
  };

  return (
    <>
      <Title title={<ForceTitle title="Documents" />} />
      <CrewCard
        title="Documents"
        icon={<FolderSpecialOutlinedIcon fontSize="large" />}
      >
        <Backdrop className={classes.backdrop} open={loading}>
          <CircularProgress color="inherit" />
        </Backdrop>
        <List component="nav">
          <SearchInput
            handleSearch={handleSearch}
            clear={clear}
            term={term}
            setTerm={setTerm}
          />
          {docs.map((itm, idx) => (
            <div key={itm.id}>
              <ListItem onClick={() => handleSelect(itm)}>
                <ListItemAvatar>
                  <Avatar>
                    <DescriptionOutlinedIcon fontSize="inherit" />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography>
                      {itm.displayName +
                        (itm.category ? " - " + itm.category : "")}
                    </Typography>
                  }
                  secondary={
                    "Created " +
                    formatDistanceToNow(new Date(itm.contentDate)) +
                    " ago"
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton edge="end" onClick={() => handleSelect(itm)}>
                    <ChevronRightIcon fontSize="large" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
              {idx < docs.length - 1 && <Divider />}
            </div>
          ))}
        </List>
      </CrewCard>
    </>
  );
};
